// EPISTEME — Main application shell (orchestrates everything)
// Uses latest browser capabilities aggressively while staying local-first and ethical.

import type { Audit, Domain, EvidenceItem, EvidenceType, Support } from './types';
import {
  createAudit, addEvidence, updateEvidence, removeEvidence, patchAudit,
  setCredence, addAssumption, addAlternative, setSufficiency,
  commitDecision, setRevision, takeSnapshot, addOutcome, addProcessCheck,
  reorderEvidence
} from './state';
import {
  computeProcessScore, meetsSufficiency, updateCredence,
  computeVoI, applyLikelihoodRatio, suggestBaseRates, BASE_RATES
} from './epistemic';
import { drawCredenceDistribution, drawMonteCarlo, renderCausalModel } from './viz';
import { renderTrustGraph } from './viz';
import {
  DOMAINS, getRandomScenario, SCENARIOS, type ScenarioSeed
} from './scenarios';
import {
  saveAudit, loadAudit, listAudits, deleteAudit, broadcastAuditUpdate,
  onAuditSync, withAuditLock, exportAll, importAudits
} from './storage';
import { encryptJSON, decryptJSON, isCryptoAvailable } from './crypto';

// ---------- App State ----------
let currentAudit: Audit | null = null;
let currentDomainFilter: Domain | null = null;
let unsubscribeSync: (() => void) | null = null;

// ---------- Utilities ----------
function $(sel: string, root: Document | HTMLElement = document): HTMLElement {
  return root.querySelector(sel) as HTMLElement;
}
function $all(sel: string, root: Document | HTMLElement = document): HTMLElement[] {
  return Array.from(root.querySelectorAll(sel));
}

function useViewTransition(fn: () => void) {
  if (!document.startViewTransition) {
    fn();
    return;
  }
  document.startViewTransition(fn);
}

function show(el: HTMLElement) { el.style.display = el.id === 'pillars' ? 'grid' : 'block'; }
function hide(el: HTMLElement) { el.style.display = 'none'; }

// ---------- Initialization ----------
async function init() {
  // PWA service worker (best effort)
  if ('serviceWorker' in navigator) {
    try { await navigator.serviceWorker.register('/sw.js'); } catch {}
  }

  // Offline indicator
  const offlineEl = $('#offline-indicator');
  const updateOffline = () => {
    if (!offlineEl) return;
    offlineEl.style.display = navigator.onLine ? 'none' : 'inline-block';
  };
  window.addEventListener('online', updateOffline);
  window.addEventListener('offline', updateOffline);
  updateOffline();

  // Domain cards on landing
  renderDomainCards();

  // Global buttons
  $('#btn-surprise')?.addEventListener('click', () => startSurprise());
  $('#btn-custom')?.addEventListener('click', () => startCustom());
  $('#btn-new')?.addEventListener('click', () => startCustom());
  $('#btn-library')?.addEventListener('click', () => showLibrary());
  $('#btn-library-close')?.addEventListener('click', () => {
    hide($('#library'));
    show($('#landing'));
  });
  $('#btn-command')?.addEventListener('click', () => openCommandPalette());
  $('#btn-close')?.addEventListener('click', closeWorkbench);

  // Add a prominent global calibration button to the nav if the element exists, otherwise inject one
  const nav = $('#nav-actions');
  if (nav) {
    const calibBtn = document.createElement('button');
    calibBtn.textContent = 'Calibration';
    calibBtn.className = 'ghost';
    calibBtn.onclick = () => showGlobalCalibration();
    nav.insertBefore(calibBtn, nav.firstChild);
  }

  // Keyboard maestro
  window.addEventListener('keydown', (e) => {
    if (e.key === '/' && (document.activeElement?.tagName === 'BODY' || document.activeElement?.tagName === 'BUTTON')) {
      e.preventDefault();
      openCommandPalette();
    }
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      openCommandPalette();
    }
    if (e.key.toLowerCase() === '?' && currentAudit) {
      e.preventDefault();
      showSocraticChallenger();
    }
  });

  // Cross-tab sync
  unsubscribeSync = onAuditSync((msg) => {
    if (msg?.type === 'audit' && currentAudit && msg.id === currentAudit.id) {
      // reload from IDB to stay in sync
      loadAudit(msg.id).then(a => { if (a) { currentAudit = a; renderWorkbench(); } });
    }
  });

  // Command palette close on outside
  const cmdk = $('#cmdk');
  cmdk.addEventListener('click', (e) => {
    if (e.target === cmdk) closeCommandPalette();
  });

  // Welcome note in console
  console.log('%c[EPISTEME] Local-first epistemology workbench ready. All cognition stays on this device.', 'color:#52525b');
}

function renderDomainCards() {
  const container = $('#domains');
  container.innerHTML = '';
  for (const d of DOMAINS) {
    const card = document.createElement('div');
    card.className = 'domain-card';
    card.innerHTML = `
      <span class="icon">${d.icon}</span>
      <div class="name">${d.label}</div>
      <div class="desc">${d.desc}</div>
    `;
    card.addEventListener('click', () => startWithDomain(d.key));
    container.appendChild(card);
  }
}

// ---------- Starting flows ----------
async function startSurprise() {
  const seed = Math.floor(Math.random() * 999999);
  const s = getRandomScenario(seed);
  await startAudit(s.domain, s.scenario, s.claim);
}

async function startWithDomain(domain: Domain) {
  const pool = SCENARIOS.filter(s => s.domain === domain);
  const s = pool[Math.floor(Math.random() * pool.length)] || getRandomScenario();
  await startAudit(s.domain, s.scenario, s.claim);
}

async function startCustom() {
  const domain = (prompt('Domain? (health, money, family, work, civic, technology, safety)', 'work') || 'work') as Domain;
  const scenario = prompt('Describe the situation briefly:', 'I need to make an important decision about...') || 'Custom situation';
  const claim = prompt('What is the precise claim or proposition you need to evaluate?', 'If I do X then Y will be true enough to justify acting.') || 'The claim under test';
  await startAudit(domain, scenario, claim);
}

async function startAudit(domain: Domain, scenario: string, claim: string, sensitive = false) {
  const audit = createAudit(domain, scenario, claim, sensitive);
  currentAudit = audit;
  await saveAudit(audit);
  broadcastAuditUpdate(audit.id);

  hide($('#landing'));
  hide($('#library'));
  const wb = $('#workbench');
  show(wb);
  wb.classList.add('active');

  renderWorkbench();
}

// ---------- Workbench rendering (the heart) ----------
function renderWorkbench() {
  if (!currentAudit) return;
  const a = currentAudit;

  // Header
  $('#wb-domain').textContent = a.domain.toUpperCase();
  $('#wb-claim').textContent = a.claim;

  // Pillars grid
  const pillars = $('#pillars');
  pillars.innerHTML = '';

  // 1. Evidence Ledger
  pillars.appendChild(renderEvidencePillar(a));

  // 2. Uncertainty + Live viz + VoI + base rates
  pillars.appendChild(renderUncertaintyPillar(a));

  // 3. Trust Graph
  pillars.appendChild(renderTrustPillar(a));

  // 3.5 Causal Model (new core feature)
  pillars.appendChild(renderCausalPillar(a));

  // 3.6 Pre-mortem & Pre-parade (new)
  pillars.appendChild(renderPreMortemPillar(a));

  // 4. Sufficiency
  pillars.appendChild(renderSufficiencyPillar(a));

  // 5. Action + Revision (combined for density)
  pillars.appendChild(renderActionRevisionPillar(a));

  // 6. Process + Simulator row (full width feel via grid)
  pillars.appendChild(renderMetacogSimulatorPillar(a));

  // Live numbers
  updateFooterMetrics(a);

  // Attach global workbench actions
  const bs = $('#btn-snapshot'); if (bs) bs.onclick = () => takeAndRenderSnapshot();
  const be = $('#btn-export'); if (be) be.onclick = () => exportCurrentAudit();
  const bc = $('#btn-calibration'); if (bc) bc.onclick = () => { showCalibration(); };
  const br = $('#btn-revise'); if (br) br.onclick = () => logOutcomeFlow();

  // Encryption / lock button (appears in workbench footer area or we inject one)
  const lockBtn = document.createElement('button');
  lockBtn.textContent = currentAudit.sensitive ? '🔒 Lock with passphrase' : 'Lock (encrypt) this audit';
  lockBtn.style.fontSize = '12px';
  lockBtn.style.padding = '6px 11px';
  lockBtn.onclick = () => lockCurrentAuditWithPassphrase();
  const footer = document.querySelector('.footer-bar');
  if (footer) footer.appendChild(lockBtn);
}

function updateFooterMetrics(a: Audit) {
  const cred = $('#credence-value');
  const proc = $('#process-value');
  if (cred) cred.textContent = `${a.uncertainty.credence}%`;
  if (proc) proc.textContent = `${computeProcessScore(a)}`;
}

function renderEvidencePillar(a: Audit): HTMLElement {
  const el = document.createElement('div');
  el.className = 'pillar';
  el.innerHTML = `
    <header>
      <span class="title">📋 Evidence Ledger</span>
      <span class="count">${a.evidence.length}</span>
    </header>
    <div class="body" style="gap:10px">
      <div style="display:flex; gap:6px; flex-wrap:wrap;">
        <button class="btn" id="add-ev">+ Add evidence</button>
        <button class="btn" id="import-clip">Paste / import</button>
      </div>
      <div id="evidence-list" style="display:flex; flex-direction:column; gap:8px; max-height: 420px; overflow:auto;"></div>
    </div>
  `;

  const list = el.querySelector('#evidence-list') as HTMLElement;

  function refreshList() {
    list.innerHTML = '';
    if (a.evidence.length === 0) {
      list.innerHTML = `<div class="empty-state">No evidence yet. Add sources, studies, observations, or testimony.</div>`;
      return;
    }
    a.evidence.forEach((ev, idx) => {
      const card = document.createElement('div');
      card.className = `evidence-card ${ev.support}`;
      card.draggable = true;

      card.innerHTML = `
        <div style="display:flex; justify-content:space-between; align-items:flex-start; gap:6px;">
          <div style="font-weight:600; font-size:13px; line-height:1.25;">${escapeHtml(ev.title)}</div>
          <div style="display:flex; gap:3px;">
            <button data-act="up" style="font-size:10px; padding:1px 5px;">↑</button>
            <button data-act="down" style="font-size:10px; padding:1px 5px;">↓</button>
            <button data-act="del" style="font-size:10px; padding:1px 5px; color:#f87171;">×</button>
          </div>
        </div>
        <div style="font-size:11.5px; color:#a1a1aa; margin:2px 0 4px;">
          ${ev.source ? escapeHtml(ev.source) + ' · ' : ''}${ev.type} · ${ev.support.toUpperCase()}
        </div>
        ${ev.quote ? `<div style="font-size:12px; font-style:italic; border-left:2px solid #3f3f46; padding-left:6px; margin:4px 0;">${escapeHtml(ev.quote.slice(0,140))}${ev.quote.length > 140 ? '…' : ''}</div>` : ''}
        <div class="cred-matrix">
          ${credBar('E', ev.credibility.expertise)}
          ${credBar('I', ev.credibility.independence)}
          ${credBar('D', ev.credibility.directness)}
          ${credBar('R', ev.credibility.recency)}
          ${credBar('C', ev.credibility.consistency)}
        </div>
        <div style="margin-top:6px; display:flex; gap:4px; align-items:center; flex-wrap:wrap;">
          <select data-field="support" style="font-size:11px; background:#111; color:#ddd; border:1px solid #27272a; border-radius:6px; padding:2px 4px;">
            <option value="pro" ${ev.support === 'pro' ? 'selected' : ''}>Supports</option>
            <option value="con" ${ev.support === 'con' ? 'selected' : ''}>Weakens</option>
            <option value="mixed" ${ev.support === 'mixed' ? 'selected' : ''}>Mixed</option>
          </select>
          <input data-field="weight" type="range" min="0.3" max="3" step="0.1" value="${ev.weight || 1}" style="width:72px;">
          <span style="font-size:10px; color:#71717a; width:22px;">${(ev.weight || 1).toFixed(1)}×</span>
          <label style="font-size:10px; color:#aaa; margin-left:4px;">LR:
            <input data-field="likelihoodRatio" type="number" step="0.1" min="0.1" max="10" value="${ev.likelihoodRatio || ''}" style="width:48px; font-size:10px; background:#111; color:#ddd; border:1px solid #27272a; border-radius:4px; padding:1px;">
          </label>
        </div>
      `;

      // interactions
      card.addEventListener('dragstart', (e) => {
        (e.dataTransfer as DataTransfer).setData('text/plain', String(idx));
      });
      card.addEventListener('dragover', (e) => e.preventDefault());
      card.addEventListener('drop', async (e) => {
        e.preventDefault();
        const from = parseInt((e.dataTransfer as DataTransfer).getData('text/plain'), 10);
        if (from === idx) return;
        const next = reorderEvidence(a, from, idx);
        await persist(next);
      });

      card.querySelectorAll('button').forEach(btn => {
        btn.addEventListener('click', async () => {
          const act = btn.getAttribute('data-act');
          if (act === 'del') {
            const next = removeEvidence(a, ev.id);
            await persist(next);
          }
          if (act === 'up' && idx > 0) {
            const next = reorderEvidence(a, idx, idx - 1);
            await persist(next);
          }
          if (act === 'down' && idx < a.evidence.length - 1) {
            const next = reorderEvidence(a, idx, idx + 1);
            await persist(next);
          }
        });
      });

      card.querySelectorAll('select, input').forEach(inp => {
        inp.addEventListener('change', async () => {
          const field = (inp as HTMLElement).getAttribute('data-field') as any;
          let val: any = (inp as HTMLInputElement).value;
          if (field === 'weight') val = parseFloat(val);
          if (field === 'likelihoodRatio') val = val ? parseFloat(val) : undefined;
          const next = updateEvidence(a, ev.id, { [field]: val });
          await persist(next);
        });
      });

      list.appendChild(card);
    });
  }

  function credBar(label: string, val: number) {
    const pct = Math.round((val / 5) * 100);
    return `<div style="font-size:9px; text-align:center;"><div class="cred-bar"><span style="width:${pct}%"></span></div><div style="color:#71717a; font-size:9px;">${label}</div></div>`;
  }

  // Add evidence
  el.querySelector('#add-ev')?.addEventListener('click', () => openEvidenceEditor(a));
  el.querySelector('#import-clip')?.addEventListener('click', () => importFromClipboard(a));

  refreshList();

  // Re-render list when parent re-renders (simple approach)
  (el as any).refresh = refreshList;
  return el;
}

async function persist(next: Audit) {
  currentAudit = next;
  await withAuditLock(next.id, () => saveAudit(next));
  broadcastAuditUpdate(next.id);
  renderWorkbench();
}

function openEvidenceEditor(a: Audit) {
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.75);z-index:300;display:flex;align-items:center;justify-content:center;padding:20px;';
  overlay.innerHTML = `
    <div style="background:#161616;border:1px solid #27272a;border-radius:16px;width:100%;max-width:520px;padding:18px;">
      <div style="font-weight:700;margin-bottom:10px;">Add Evidence</div>
      <input id="ev-title" placeholder="Title / key claim" style="width:100%;background:#111;border:1px solid #27272a;color:#eee;padding:9px;border-radius:8px;margin-bottom:8px;">
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
        <input id="ev-source" placeholder="Source / author" style="background:#111;border:1px solid #27272a;color:#eee;padding:8px;border-radius:8px;">
        <select id="ev-type" style="background:#111;border:1px solid #27272a;color:#eee;padding:8px;border-radius:8px;">
          <option value="study">Study / paper</option>
          <option value="expert">Expert opinion</option>
          <option value="data">Dataset / stats</option>
          <option value="testimony">Testimony / anecdote</option>
          <option value="personal">Personal observation</option>
          <option value="authority">Authority / institution</option>
          <option value="leak">Leak / internal doc</option>
          <option value="article">Article / reporting</option>
          <option value="artifact">Physical / artifact</option>
          <option value="other">Other</option>
        </select>
      </div>
      <textarea id="ev-quote" placeholder="Key quote or extract (optional)" rows="2" style="width:100%;margin-top:8px;background:#111;border:1px solid #27272a;color:#eee;padding:8px;border-radius:8px;"></textarea>
      <div style="margin:10px 0 6px;font-size:12px;color:#a1a1aa;">Support direction</div>
      <div style="display:flex;gap:8px;">
        <label><input type="radio" name="sup" value="pro" checked> Supports</label>
        <label><input type="radio" name="sup" value="con"> Weakens</label>
        <label><input type="radio" name="sup" value="mixed"> Mixed / unclear</label>
      </div>
      <div style="margin-top:12px;font-size:12px;color:#a1a1aa;">Credibility (0-5)</div>
      <div style="display:grid;grid-template-columns:repeat(5,1fr);gap:6px;font-size:11px;">
        ${['Expertise','Independence','Directness','Recency','Consistency'].map((lab,i) => `
          <div>${lab}<br><input type="range" min="0" max="5" step="1" value="3" id="cred-${i}"></div>
        `).join('')}
      </div>
      <div style="margin-top:16px;display:flex;gap:8px;justify-content:flex-end;">
        <button id="ev-cancel">Cancel</button>
        <button id="ev-save" class="primary">Add to ledger</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const save = async () => {
    const title = (overlay.querySelector('#ev-title') as HTMLInputElement).value.trim() || 'Untitled evidence';
    const source = (overlay.querySelector('#ev-source') as HTMLInputElement).value.trim() || undefined;
    const type = (overlay.querySelector('#ev-type') as HTMLSelectElement).value as EvidenceType;
    const quote = (overlay.querySelector('#ev-quote') as HTMLTextAreaElement).value.trim() || undefined;
    const support = (overlay.querySelector('input[name="sup"]:checked') as HTMLInputElement).value as Support;

    const cred: any = {};
    ['Expertise','Independence','Directness','Recency','Consistency'].forEach((_, i) => {
      const val = parseInt((overlay.querySelector(`#cred-${i}`) as HTMLInputElement).value, 10);
      const key = ['expertise','independence','directness','recency','consistency'][i];
      (cred as any)[key] = isNaN(val) ? 3 : val;
    });

    const next = addEvidence(a, {
      type, support, title, source, quote, credibility: cred as any, weight: 1, date: undefined
    });
    overlay.remove();
    await persist(next);
  };

  overlay.querySelector('#ev-save')?.addEventListener('click', save);
  overlay.querySelector('#ev-cancel')?.addEventListener('click', () => overlay.remove());
  (overlay.querySelector('#ev-title') as HTMLInputElement)?.focus();
}

async function importFromClipboard(a: Audit) {
  try {
    const text = await navigator.clipboard.readText();
    if (!text) throw new Error('empty');
    const title = text.split('\n')[0].slice(0, 110);
    const next = addEvidence(a, {
      type: 'article',
      support: 'mixed',
      title: title || 'Pasted from clipboard',
      quote: text.slice(0, 420),
      credibility: { expertise: 2, independence: 2, directness: 2, recency: 3, consistency: 2 },
      weight: 0.8,
    } as any);
    await persist(next);
  } catch {
    alert('Could not read clipboard. You can still add evidence manually.');
  }
}

function renderUncertaintyPillar(a: Audit): HTMLElement {
  const el = document.createElement('div');
  el.className = 'pillar';
  el.innerHTML = `
    <header>
      <span class="title">🌫️ Uncertainty</span>
    </header>
    <div class="body">
      <div>
        <div class="section-label">Current credence</div>
        <input type="range" id="cred-slider" min="2" max="98" step="1" value="${a.uncertainty.credence}" style="width:100%;">
        <div style="display:flex;justify-content:space-between;font-size:11px;font-family:monospace;color:#a1a1aa;">
          <div>Very unlikely</div><div id="cred-readout" style="font-weight:600;color:#f5f5f5;">${a.uncertainty.credence}%</div><div>Almost certain</div>
        </div>
      </div>

      <div class="canvas-wrap"><canvas id="cred-dist" style="width:100%;height:118px;"></canvas></div>

      <div>
        <div class="section-label">Assumptions (what must be true)</div>
        <div id="assumptions" style="display:flex;flex-direction:column;gap:4px;"></div>
        <button class="btn" style="margin-top:4px;font-size:12px;padding:5px 11px;" id="add-assump">+ Add assumption</button>
      </div>

      <div>
        <div class="section-label">Strongest alternative explanations (claim is false)</div>
        <div id="alts" style="display:flex;flex-direction:column;gap:4px;"></div>
        <button class="btn" style="margin-top:4px;font-size:12px;padding:5px 11px;" id="add-alt">+ Add alternative</button>
      </div>
    </div>
  `;

  const slider = el.querySelector('#cred-slider') as HTMLInputElement;
  const readout = el.querySelector('#cred-readout') as HTMLElement;
  const canvas = el.querySelector('#cred-dist') as HTMLCanvasElement;

  function refreshViz() {
    readout.textContent = `${a.uncertainty.credence}%`;
    drawCredenceDistribution(canvas, a.uncertainty.credence, a.evidence);
  }

  slider.addEventListener('input', async () => {
    const next = setCredence(a, parseInt(slider.value, 10));
    currentAudit = next;
    a = next;
    refreshViz();
    updateFooterMetrics(a);
  });
  slider.addEventListener('change', async () => {
    await persist(a);
  });

  // assumptions
  const assumpList = el.querySelector('#assumptions') as HTMLElement;
  function renderAssumps() {
    assumpList.innerHTML = '';
    a.uncertainty.assumptions.forEach((txt, i) => {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;gap:6px;align-items:center;font-size:13px;';
      row.innerHTML = `<span style="flex:1;">• ${escapeHtml(txt)}</span><button style="font-size:11px;padding:1px 6px;">×</button>`;
      row.querySelector('button')!.addEventListener('click', async () => {
        const next = patchAudit(a, { uncertainty: { ...a.uncertainty, assumptions: a.uncertainty.assumptions.filter((_, ii) => ii !== i) } });
        await persist(next);
      });
      assumpList.appendChild(row);
    });
  }
  el.querySelector('#add-assump')?.addEventListener('click', async () => {
    const txt = prompt('What must be true for the current evidence to count?', '');
    if (txt && txt.trim()) {
      const next = addAssumption(a, txt.trim());
      await persist(next);
    }
  });

  // alternatives
  const altList = el.querySelector('#alts') as HTMLElement;
  function renderAlts() {
    altList.innerHTML = '';
    a.uncertainty.alternatives.forEach((txt, i) => {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;gap:6px;align-items:center;font-size:13px;';
      row.innerHTML = `<span style="flex:1;">• ${escapeHtml(txt)}</span><button style="font-size:11px;padding:1px 6px;">×</button>`;
      row.querySelector('button')!.addEventListener('click', async () => {
        const next = patchAudit(a, { uncertainty: { ...a.uncertainty, alternatives: a.uncertainty.alternatives.filter((_, ii) => ii !== i) } });
        await persist(next);
      });
      altList.appendChild(row);
    });
  }
  el.querySelector('#add-alt')?.addEventListener('click', async () => {
    const txt = prompt('Best story in which the claim is false or much weaker?', '');
    if (txt && txt.trim()) {
      const next = addAlternative(a, txt.trim());
      await persist(next);
    }
  });

  renderAssumps();
  renderAlts();
  setTimeout(refreshViz, 30);

  // live refresh when evidence changes externally
  (el as any).refreshAll = () => { renderAssumps(); renderAlts(); refreshViz(); };

  // === VoI + Base Rate suggestions (core new feature) ===
  const voiDiv = document.createElement('div');
  voiDiv.style.marginTop = '8px';
  voiDiv.style.fontSize = '12px';
  const voi = computeVoI(a);
  voiDiv.innerHTML = `<div class="section-label">Value of Information</div><div>${voi.explanation}</div>`;
  el.querySelector('.body')?.appendChild(voiDiv);

  const brDiv = document.createElement('div');
  brDiv.style.marginTop = '8px';
  brDiv.innerHTML = `<div class="section-label">Reference class / base rate suggestions</div>`;
  const brList = document.createElement('div');
  brList.style.fontSize = '11px';
  const suggestions = suggestBaseRates(a.domain);
  brList.innerHTML = suggestions.length ? suggestions.map(s => `• ${s.rate} <button data-key="${s.key}" style="font-size:9px;padding:1px 4px;">use as prior</button>`).join('<br>') : '<em>No specific suggestions for domain.</em>';
  brDiv.appendChild(brList);
  el.querySelector('.body')?.appendChild(brDiv);

  brList.querySelectorAll('button').forEach(btn => {
    btn.addEventListener('click', async () => {
      const key = (btn as HTMLElement).getAttribute('data-key')!;
      const note = BASE_RATES[key]?.rate || '';
      const next = patchAudit(a, { uncertainty: { ...a.uncertainty, baseRate: note } });
      await persist(next);
    });
  });

  return el;
}

function renderTrustPillar(a: Audit): HTMLElement {
  const el = document.createElement('div');
  el.className = 'pillar';
  el.innerHTML = `
    <header>
      <span class="title">🤝 Trust Map</span>
      <span class="count">${a.trust.nodes.length}</span>
    </header>
    <div class="body">
      <div id="trust-graph" style="min-height:260px;"></div>
      <div style="display:flex;gap:6px;margin-top:6px;flex-wrap:wrap;">
        <button class="btn" id="add-node" style="font-size:12px;padding:5px 11px;">+ Add source / actor</button>
        <button class="btn" id="add-edge" style="font-size:12px;padding:5px 11px;">Add relation</button>
      </div>
      <div style="font-size:11px;color:#71717a;margin-top:4px;">Double-click nodes to rename. Drag to reposition.</div>
    </div>
  `;

  const graphContainer = el.querySelector('#trust-graph') as HTMLElement;

  function refreshGraph() {
    renderTrustGraph(graphContainer, a.trust, async (newGraph) => {
      const next = patchAudit(a, { trust: newGraph });
      await persist(next);
    });
  }

  el.querySelector('#add-node')?.addEventListener('click', async () => {
    const label = prompt('Name of person, org, study, or institution?', 'New source');
    if (!label) return;
    const kind = (prompt('Kind? (person/org/study/media/gov/other)', 'person') || 'person') as any;
    const next = patchAudit(a, {
      trust: {
        nodes: [...a.trust.nodes, { id: crypto.randomUUID(), label, kind, trackRecord: 0, incentive: 0, disclosure: 1 }],
        edges: a.trust.edges
      }
    });
    await persist(next);
  });

  el.querySelector('#add-edge')?.addEventListener('click', async () => {
    if (a.trust.nodes.length < 2) { alert('Need at least two nodes'); return; }
    const from = prompt('From node label (exact):', a.trust.nodes[0].label);
    const to = prompt('To node label (exact):', a.trust.nodes[1].label);
    const rel = (prompt('Relation? (cites/conflicts/funds/quotes/relies)', 'cites') || 'cites') as any;
    if (!from || !to) return;
    const n1 = a.trust.nodes.find(n => n.label === from);
    const n2 = a.trust.nodes.find(n => n.label === to);
    if (!n1 || !n2) { alert('Could not find nodes'); return; }
    const next = patchAudit(a, {
      trust: { nodes: a.trust.nodes, edges: [...a.trust.edges, { from: n1.id, to: n2.id, relation: rel }] }
    });
    await persist(next);
  });

  setTimeout(refreshGraph, 40);
  return el;
}

function renderCausalPillar(a: Audit): HTMLElement {
  const el = document.createElement('div');
  el.className = 'pillar';
  el.innerHTML = `
    <header>
      <span class="title">🧬 Causal Model</span>
    </header>
    <div class="body">
      <div id="causal-graph" style="min-height:240px;"></div>
      <div style="display:flex;gap:6px;margin-top:6px;flex-wrap:wrap;">
        <button class="btn" id="add-cnode" style="font-size:12px;padding:5px 11px;">+ Variable</button>
        <button class="btn" id="add-cedge" style="font-size:12px;padding:5px 11px;">Add influence</button>
      </div>
      <div style="font-size:11px;color:#71717a;margin-top:4px;">Drag nodes. Double-click to rename. Models what affects the claim.</div>
    </div>
  `;

  const graphContainer = el.querySelector('#causal-graph') as HTMLElement;
  const model = a.causal || { nodes: [], edges: [] };

  function refreshCausal() {
    renderCausalModel(graphContainer, model, async (newModel) => {
      const next = patchAudit(a, { causal: newModel });
      await persist(next);
    });
  }

  el.querySelector('#add-cnode')?.addEventListener('click', async () => {
    const label = prompt('Variable or factor name?', 'New factor');
    if (!label) return;
    const next = patchAudit(a, {
      causal: {
        nodes: [...(a.causal?.nodes || []), { id: crypto.randomUUID(), label }],
        edges: a.causal?.edges || []
      }
    });
    await persist(next);
  });

  el.querySelector('#add-cedge')?.addEventListener('click', async () => {
    const nodes = a.causal?.nodes || [];
    if (nodes.length < 2) { alert('Need at least 2 variables'); return; }
    const fromL = prompt('From (label):', nodes[0].label);
    const toL = prompt('To (label):', nodes[1].label);
    const note = prompt('Influence note (optional):', '') || undefined;
    if (!fromL || !toL) return;
    const n1 = nodes.find(n => n.label === fromL);
    const n2 = nodes.find(n => n.label === toL);
    if (!n1 || !n2) { alert('Labels not found'); return; }
    const next = patchAudit(a, {
      causal: {
        nodes,
        edges: [...(a.causal?.edges || []), { from: n1.id, to: n2.id, note }]
      }
    });
    await persist(next);
  });

  setTimeout(refreshCausal, 50);
  return el;
}

function renderPreMortemPillar(a: Audit): HTMLElement {
  const el = document.createElement('div');
  el.className = 'pillar';
  const morts = a.preMortems || [];
  const parades = a.preParades || [];
  el.innerHTML = `
    <header><span class="title">🪦 Pre-mortem / 🎉 Pre-parade</span></header>
    <div class="body" style="font-size:12px;">
      <div><strong>Pre-mortem</strong> (assume decision failed badly in 6mo – what went wrong?)</div>
      <div id="mort-list"></div>
      <button id="add-mort" class="btn" style="font-size:11px;margin-top:4px;">+ Add failure scenario</button>

      <div style="margin-top:10px;"><strong>Pre-parade</strong> (assume it succeeded spectacularly – what enabled it?)</div>
      <div id="parade-list"></div>
      <button id="add-parade" class="btn" style="font-size:11px;margin-top:4px;">+ Add success enabler</button>
    </div>
  `;

  function renderLists() {
    const mlist = el.querySelector('#mort-list') as HTMLElement;
    mlist.innerHTML = morts.map((m,i) => `<div style="margin:3px 0;">• ${escapeHtml(m)} <button data-i="${i}" data-t="mort" style="font-size:9px;">×</button></div>`).join('');
    const plist = el.querySelector('#parade-list') as HTMLElement;
    plist.innerHTML = parades.map((p,i) => `<div style="margin:3px 0;">• ${escapeHtml(p)} <button data-i="${i}" data-t="parade" style="font-size:9px;">×</button></div>`).join('');
    mlist.querySelectorAll('button').forEach(b => b.addEventListener('click', async () => {
      const i = parseInt((b as HTMLElement).getAttribute('data-i')!);
      const next = patchAudit(a, { preMortems: a.preMortems.filter((_,ii)=>ii!==i) });
      await persist(next);
    }));
    plist.querySelectorAll('button').forEach(b => b.addEventListener('click', async () => {
      const i = parseInt((b as HTMLElement).getAttribute('data-i')!);
      const next = patchAudit(a, { preParades: a.preParades.filter((_,ii)=>ii!==i) });
      await persist(next);
    }));
  }

  el.querySelector('#add-mort')?.addEventListener('click', async () => {
    const txt = prompt('What could cause failure?', '');
    if (txt) {
      const next = patchAudit(a, { preMortems: [...(a.preMortems||[]), txt] });
      await persist(next);
    }
  });
  el.querySelector('#add-parade')?.addEventListener('click', async () => {
    const txt = prompt('What would enable great success?', '');
    if (txt) {
      const next = patchAudit(a, { preParades: [...(a.preParades||[]), txt] });
      await persist(next);
    }
  });

  setTimeout(renderLists, 30);
  return el;
}

function renderSufficiencyPillar(a: Audit): HTMLElement {
  const el = document.createElement('div');
  el.className = 'pillar';
  const suf = a.sufficiency;

  el.innerHTML = `
    <header><span class="title">🎯 Sufficiency Threshold</span></header>
    <div class="body">
      <div class="sufficiency">
        <label>Minimum strong independent sources: <input type="number" id="min-src" value="${suf.minSources}" min="0" max="12" style="width:54px;"></label>
        <label>My credence must reach: <input type="number" id="min-cred" value="${suf.minCredence}" min="10" max="98" style="width:54px;">%</label>
        <label>Max strong disconfirmers allowed: <input type="number" id="max-dis" value="${suf.maxDisconfirmer}" min="0" max="6" style="width:54px;"></label>
        <label>Required days of no counter-evidence (optional): <input type="number" id="req-days" value="${suf.requiredDays || ''}" placeholder="—" style="width:54px;"></label>
      </div>
      <div>
        <div class="section-label" style="margin-top:8px;">Plain language rule (required)</div>
        <textarea id="suf-text" style="width:100%;min-height:64px;background:#111;border:1px solid #27272a;color:#ddd;border-radius:8px;padding:8px;font-size:13px;">${escapeHtml(suf.textual)}</textarea>
      </div>
      <div style="margin-top:6px;">
        <button id="save-suf" class="btn" style="font-size:12px;">Save threshold</button>
        <span id="suf-status" style="margin-left:10px;font-size:12px;"></span>
      </div>
    </div>
  `;

  const status = el.querySelector('#suf-status') as HTMLElement;

  el.querySelector('#save-suf')?.addEventListener('click', async () => {
    const minSources = parseInt((el.querySelector('#min-src') as HTMLInputElement).value, 10) || 0;
    const minCredence = parseInt((el.querySelector('#min-cred') as HTMLInputElement).value, 10) || 50;
    const maxDis = parseInt((el.querySelector('#max-dis') as HTMLInputElement).value, 10) || 0;
    const reqDays = parseInt((el.querySelector('#req-days') as HTMLInputElement).value, 10) || undefined;
    const textual = (el.querySelector('#suf-text') as HTMLTextAreaElement).value.trim();

    const next = setSufficiency(a, { minSources, minCredence, maxDisconfirmer: maxDis, requiredDays: reqDays, textual });
    await persist(next);
    status.textContent = meetsSufficiency(next) ? '✓ Currently meets your bar' : 'Not yet sufficient';
    setTimeout(() => status.textContent = '', 1800);
  });

  // initial status
  setTimeout(() => {
    status.textContent = meetsSufficiency(a) ? '✓ Currently meets your bar' : '';
  }, 80);

  return el;
}

function renderActionRevisionPillar(a: Audit): HTMLElement {
  const el = document.createElement('div');
  el.className = 'pillar';
  el.innerHTML = `
    <header><span class="title">✅ Action + Revision</span></header>
    <div class="body" style="gap:14px;">
      <div>
        <div class="section-label">Committed action (what you will actually do)</div>
        ${a.decision ? `
          <div class="commit-box">
            <div style="font-weight:600;">${escapeHtml(a.decision.action)}</div>
            <div style="margin-top:6px;font-size:13px;opacity:0.9;">${escapeHtml(a.decision.rationale)}</div>
            <div style="margin-top:8px;font-size:11px;color:#71717a;">Committed at credence ${a.decision.confidenceAtCommit}%</div>
          </div>
        ` : `
          <textarea id="action-text" placeholder="I will..." style="width:100%;min-height:52px;background:#111;border:1px solid #27272a;border-radius:8px;padding:8px;"></textarea>
          <textarea id="rationale-text" placeholder="Rationale (at least 2-3 sentences)..." style="width:100%;min-height:58px;margin-top:6px;background:#111;border:1px solid #27272a;border-radius:8px;padding:8px;"></textarea>
          <button id="commit-btn" class="primary" style="margin-top:8px;">Commit to this action</button>
        `}
      </div>

      <div>
        <div class="section-label">Revision protocol</div>
        <div style="font-size:12.5px;margin-bottom:4px;">Next scheduled review</div>
        <input type="date" id="next-review" value="${a.revision.nextReview || ''}" style="background:#111;border:1px solid #27272a;color:#ddd;padding:6px;border-radius:6px;">
        <div style="margin-top:8px;font-size:12.5px;">Falsifiers (specific things that would make the claim false)</div>
        <textarea id="falsifiers" style="width:100%;min-height:44px;background:#111;border:1px solid #27272a;border-radius:8px;padding:6px;font-size:12.5px;">${a.revision.falsifiers.join('\n')}</textarea>
        <div style="margin-top:6px;font-size:12.5px;">What I will actively monitor (cheap signals)</div>
        <textarea id="monitors" style="width:100%;min-height:44px;background:#111;border:1px solid #27272a;border-radius:8px;padding:6px;font-size:12.5px;">${a.revision.monitors.join('\n')}</textarea>
        <button id="save-rev" class="btn" style="margin-top:8px;font-size:12px;">Save revision plan</button>
      </div>
    </div>
  `;

  if (!a.decision) {
    el.querySelector('#commit-btn')?.addEventListener('click', async () => {
      const action = (el.querySelector('#action-text') as HTMLTextAreaElement).value.trim();
      const rationale = (el.querySelector('#rationale-text') as HTMLTextAreaElement).value.trim();
      if (!action || rationale.length < 20) {
        alert('Please write a clear action and a real rationale (several sentences).');
        return;
      }
      const next = commitDecision(a, action, rationale);
      await persist(next);
    });
  }

  el.querySelector('#save-rev')?.addEventListener('click', async () => {
    const nextReview = (el.querySelector('#next-review') as HTMLInputElement).value || undefined;
    const fals = (el.querySelector('#falsifiers') as HTMLTextAreaElement).value.split('\n').map(s => s.trim()).filter(Boolean);
    const mon = (el.querySelector('#monitors') as HTMLTextAreaElement).value.split('\n').map(s => s.trim()).filter(Boolean);
    const next = setRevision(a, { nextReview, falsifiers: fals, monitors: mon });
    await persist(next);
  });

  return el;
}

function renderMetacogSimulatorPillar(a: Audit): HTMLElement {
  const el = document.createElement('div');
  el.className = 'pillar';
  el.style.gridColumn = '1 / -1';
  el.innerHTML = `
    <header><span class="title">🧠 Metacognition + Simulator</span></header>
    <div class="body">
      <div class="viz-row">
        <div>
          <div class="section-label">Process quality checklist (check as you go)</div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:4px 12px;font-size:13px;">
            <label><input type="checkbox" id="chk-dis"> I actively sought disconfirming evidence</label>
            <label><input type="checkbox" id="chk-alt"> I wrote the strongest alternative stories</label>
            <label><input type="checkbox" id="chk-pre"> I defined sufficiency criteria before committing</label>
            <label><input type="checkbox" id="chk-base"> I considered base rates / reference classes</label>
          </div>
          <div style="margin-top:6px;display:flex;gap:10px;align-items:center;">
            <div>Affect / motivation (0 calm – 5 very charged): <input type="range" id="affect" min="0" max="5" value="2"></div>
            <div>Fatigue: <input type="range" id="fatigue" min="0" max="5" value="2"></div>
          </div>
          <button id="save-process" class="btn" style="margin-top:8px;font-size:12px;">Log reflection</button>
          <div id="process-msg" style="margin-top:4px;font-size:12px;color:#4ade80;"></div>
        </div>
        <div>
          <div class="section-label">Monte Carlo: "If I act now, simulated outcomes"</div>
          <div class="canvas-wrap"><canvas id="mc-canvas" style="width:100%;height:132px;"></canvas></div>
          <button id="rerun-mc" class="btn" style="margin-top:6px;font-size:12px;">Re-run simulation</button>
        </div>
      </div>
      <div style="margin-top:8px;font-size:12.5px;color:#a1a1aa;">
        Socratic challenger: press <strong>?</strong> anytime for adversarial prompts. Your process score updates live in the footer.
      </div>
    </div>
  `;

  // Process checks
  const saveBtn = el.querySelector('#save-process') as HTMLButtonElement;
  saveBtn.addEventListener('click', async () => {
    const check = addProcessCheck(a, {
      soughtDisconfirmers: (el.querySelector('#chk-dis') as HTMLInputElement).checked,
      wroteAlternatives: (el.querySelector('#chk-alt') as HTMLInputElement).checked,
      preCommitted: (el.querySelector('#chk-pre') as HTMLInputElement).checked,
      consideredBaseRate: (el.querySelector('#chk-base') as HTMLInputElement).checked,
      affectCheck: parseInt((el.querySelector('#affect') as HTMLInputElement).value, 10),
      fatigue: parseInt((el.querySelector('#fatigue') as HTMLInputElement).value, 10),
    });
    const next = patchAudit(a, { processChecks: [...a.processChecks, check] });
    await persist(next);
    (el.querySelector('#process-msg') as HTMLElement).textContent = 'Reflection recorded. Thank you.';
    setTimeout(() => (el.querySelector('#process-msg') as HTMLElement).textContent = '', 1600);
  });

  // Monte Carlo
  const mcCanvas = el.querySelector('#mc-canvas') as HTMLCanvasElement;
  const drawMC = () => drawMonteCarlo(mcCanvas, a);
  setTimeout(drawMC, 60);
  el.querySelector('#rerun-mc')?.addEventListener('click', drawMC);

  return el;
}

// ---------- Snapshots, exports, library, calibration ----------
async function takeAndRenderSnapshot() {
  if (!currentAudit) return;
  const label = prompt('Optional snapshot label (e.g. "After new study")', '') || undefined;
  const next = takeSnapshot(currentAudit, label);
  await persist(next);
  alert(`Snapshot taken at ${new Date(next.snapshots[next.snapshots.length - 1].takenAt).toLocaleString()}`);
}

async function exportCurrentAudit() {
  if (!currentAudit) return;
  const a = currentAudit;

  const visuals = captureCurrentVisuals();
  const hasVisuals = !!(visuals.credenceImg || visuals.monteCarloImg || visuals.trustSvgDataUrl);

  // Offer rich self-contained HTML when possible
  const wantRich = confirm(
    hasVisuals
      ? 'Export rich self-contained HTML report with embedded charts & graphs? (Cancel = classic printable version)'
      : 'Export classic printable report? (Cancel = minimal self-contained bundle)'
  );

  if (wantRich && hasVisuals) {
    const json = JSON.stringify(cloneForExport(a), null, 2);
    const bundle = buildRichSelfContainedHTML(a, json, visuals);
    download(`episteme-audit-${a.id.slice(0,8)}.html`, bundle, 'text/html');

    // File System Access (latest browser tech) - optional save to disk folder
    if ('showSaveFilePicker' in window) {
      if (confirm('Also save the bundle to a local folder using File System Access?')) {
        try {
          // @ts-ignore
          const handle = await (window as any).showSaveFilePicker({ suggestedName: `episteme-audit-${a.id.slice(0,8)}.html` });
          const writable = await handle.createWritable();
          await writable.write(bundle);
          await writable.close();
        } catch(e) { /* user cancelled or not supported */ }
      }
    }
  } else if (wantRich === false || !hasVisuals) {
    // Printable
    const win = window.open('', '_blank');
    if (!win) { alert('Popup blocked'); return; }
    const html = buildPrintableReport(a);
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 420);
  } else {
    // Fallback minimal bundle
    const json = JSON.stringify(cloneForExport(a), null, 2);
    const bundle = buildSelfContainedHTML(a, json);
    download(`episteme-audit-${a.id.slice(0,8)}.html`, bundle, 'text/html');
  }
}

function buildPrintableReport(a: Audit): string {
  const score = computeProcessScore(a);
  const evHtml = a.evidence.map(e => {
    const credAvg = ((e.credibility.expertise + e.credibility.independence + e.credibility.directness + e.credibility.recency + e.credibility.consistency) / 5).toFixed(1);
    return `<div style="margin:12px 0;padding:10px;border-left:4px solid ${e.support==='pro'?'#166534':e.support==='con'?'#9f1239':'#854d0e'};background:#f8f8f8;">
      <strong>${escapeHtml(e.title)}</strong> — <em>${e.support}</em> (${e.type}) &nbsp; <small>avg cred ${credAvg}</small><br>
      <small>${escapeHtml(e.source || '')} ${e.date ? '· ' + escapeHtml(e.date) : ''}</small>
      ${e.quote ? `<div style="margin-top:4px;font-style:italic;border-left:2px solid #ccc;padding-left:8px;">${escapeHtml(e.quote)}</div>` : ''}
    </div>`;
  }).join('');

  return `<!doctype html><html><head><meta charset="utf-8"><title>EPISTEME Audit — ${escapeHtml(a.claim.slice(0,70))}</title>
  <style>
    body{font-family:Inter,system-ui;margin:32px;line-height:1.5;color:#111;max-width:860px}
    h1,h2{font-weight:600}
    .section{margin-bottom:32px}
    .meta{color:#555;font-size:13px}
    .ev-card { margin:8px 0; }
  </style>
  </head><body>
  <h1>EPISTEME — Epistemic Audit</h1>
  <div class="meta">Domain: ${a.domain.toUpperCase()} • Created ${new Date(a.created).toLocaleString()} • Process quality: ${score}/100 • Final credence: ${a.uncertainty.credence}%</div>

  <div class="section"><h2>Situation</h2><p>${escapeHtml(a.scenario)}</p></div>
  <div class="section"><h2>The Claim Under Test</h2><p><strong>${escapeHtml(a.claim)}</strong></p></div>

  <div class="section"><h2>Evidence Ledger (${a.evidence.length})</h2>${evHtml || '<em>No evidence recorded.</em>'}</div>

  <div class="section"><h2>Uncertainty &amp; Assumptions</h2>
    <p><strong>Current credence:</strong> ${a.uncertainty.credence}%</p>
    <p><strong>Key assumptions:</strong></p><ul>${a.uncertainty.assumptions.map(x => `<li>${escapeHtml(x)}</li>`).join('') || '<li><em>none logged</em></li>'}</ul>
    <p><strong>Leading alternative explanations:</strong></p><ul>${a.uncertainty.alternatives.map(x => `<li>${escapeHtml(x)}</li>`).join('') || '<li><em>none logged</em></li>'}</ul>
  </div>

  <div class="section"><h2>Sufficiency Threshold</h2><p>${escapeHtml(a.sufficiency.textual)}</p></div>

  ${a.decision ? `<div class="section"><h2>Committed Action</h2><p><strong>${escapeHtml(a.decision.action)}</strong></p><p>${escapeHtml(a.decision.rationale)}</p><p><small>Committed while at ${a.decision.confidenceAtCommit}% credence</small></p></div>` : ''}

  <div class="section"><h2>Revision Protocol</h2>
    <p><strong>Next review:</strong> ${a.revision.nextReview || 'not scheduled'}</p>
    <p><strong>Falsifiers to watch:</strong></p><ul>${a.revision.falsifiers.map(f => `<li>${escapeHtml(f)}</li>`).join('') || '<li><em>none</em></li>'}</ul>
    <p><strong>Monitors / cheap signals:</strong></p><ul>${a.revision.monitors.map(m => `<li>${escapeHtml(m)}</li>`).join('') || '<li><em>none</em></li>'}</ul>
  </div>

  ${a.outcomes.length ? `<div class="section"><h2>Outcome Log</h2>${a.outcomes.map(o => `<div style="margin-bottom:8px"><strong>${new Date(o.loggedAt).toLocaleDateString()}</strong> — ${o.result}: ${escapeHtml(o.outcome)} ${o.lessons ? '<br><small>Learned: '+escapeHtml(o.lessons)+'</small>' : ''}</div>`).join('')}</div>` : ''}

  <div style="margin-top:60px;font-size:12px;color:#666;border-top:1px solid #ddd;padding-top:12px">Exported from EPISTEME — your reasoning, your device, your responsibility.</div>
  </body></html>`;
}

// Minimal fallback bundle (kept for compatibility)
function buildSelfContainedHTML(a: Audit, json: string): string {
  return `<!doctype html>
<html><head><meta charset="utf-8"><title>EPISTEME • Self-contained audit</title>
<style>body{margin:0;font-family:system-ui;background:#0a0a0a;color:#f5f5f5;padding:20px;line-height:1.5} pre{background:#111;padding:16px;overflow:auto;border-radius:8px;font-size:12px}</style>
</head><body>
<h1>EPISTEME — Self-contained Audit</h1>
<p>This file contains your complete reasoning + data. Open in any browser. Nothing phones home.</p>
<h2>Claim</h2><p>${escapeHtml(a.claim)}</p>
<h2>Current Credence</h2><p>${a.uncertainty.credence}%</p>
<h2>Full Data (JSON)</h2>
<pre>${escapeHtml(json)}</pre>
<script>console.log('%c[EPISTEME] Self-contained export loaded. You own this data.', 'color:#52525b');</script>
</body></html>`;
}

// The good one — rich, with embedded images when available
function buildRichSelfContainedHTML(
  a: Audit,
  json: string,
  visuals: { credenceImg: string; monteCarloImg: string; trustSvgDataUrl: string }
): string {
  const score = computeProcessScore(a);
  const evList = a.evidence.map(e => {
    const avg = ((e.credibility.expertise + e.credibility.independence + e.credibility.directness + e.credibility.recency + e.credibility.consistency) / 5).toFixed(1);
    return `<div style="border:1px solid #333;margin:8px 0;padding:10px;border-radius:6px;background:#111;color:#ddd;font-size:13px">
      <strong>${escapeHtml(e.title)}</strong> <span style="color:#888">(${e.type} • ${e.support} • avg cred ${avg})</span><br>
      ${e.source ? `<small>${escapeHtml(e.source)}</small>` : ''}
      ${e.quote ? `<div style="margin-top:6px;font-style:italic;opacity:0.85;border-left:3px solid #444;padding-left:8px">${escapeHtml(e.quote)}</div>` : ''}
    </div>`;
  }).join('');

  const visualsSection = `
    ${visuals.credenceImg ? `<div style="margin:16px 0"><h3 style="margin:4px 0;font-size:14px">Credence Distribution</h3><img src="${visuals.credenceImg}" style="max-width:100%;border:1px solid #222;border-radius:4px;background:#000"></div>` : ''}
    ${visuals.monteCarloImg ? `<div style="margin:16px 0"><h3 style="margin:4px 0;font-size:14px">Monte Carlo Simulation (if you act)</h3><img src="${visuals.monteCarloImg}" style="max-width:100%;border:1px solid #222;border-radius:4px;background:#000"></div>` : ''}
    ${visuals.trustSvgDataUrl ? `<div style="margin:16px 0"><h3 style="margin:4px 0;font-size:14px">Trust Map</h3><img src="${visuals.trustSvgDataUrl}" style="max-width:100%;border:1px solid #222;border-radius:4px;background:#000"></div>` : ''}
  `;

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>EPISTEME • ${escapeHtml(a.claim.slice(0,80))}</title>
<style>
  :root { color-scheme: dark; }
  body { margin:0; font-family: system-ui, -apple-system, Inter, sans-serif; background:#0a0a0a; color:#f1f1f1; line-height:1.5; padding: 20px 24px; }
  .container { max-width: 980px; margin: 0 auto; }
  h1 { font-size: 28px; margin: 8px 0 4px; }
  h2 { font-size: 18px; margin: 24px 0 8px; border-bottom: 1px solid #222; padding-bottom: 4px; }
  h3 { font-size: 14px; margin: 12px 0 6px; color:#aaa; }
  .meta { font-size: 12px; color:#888; margin-bottom: 16px; }
  .section { margin-bottom: 28px; }
  .card { background:#161616; border:1px solid #27272a; border-radius:8px; padding:12px 14px; margin-bottom:8px; }
  pre { background:#111; padding:14px; border-radius:6px; overflow:auto; font-size:11px; }
  .pill { display:inline-block; font-size:10px; padding:1px 7px; border-radius:999px; background:#222; margin-left:6px; }
  footer { margin-top:60px; font-size:11px; color:#555; border-top:1px solid #222; padding-top:12px; }
  .toolbar { position:sticky; top:0; background:#0a0a0a; padding:8px 0; z-index:10; border-bottom:1px solid #222; margin-bottom:12px; }
</style>
</head>
<body>
<div class="container">
  <div class="toolbar">
    <strong>EPISTEME</strong> — Self-contained Epistemic Audit &nbsp;
    <span class="pill">${a.domain}</span>
    <span class="pill">Process: ${score}</span>
    <span class="pill">Credence: ${a.uncertainty.credence}%</span>
  </div>

  <h1>${escapeHtml(a.claim)}</h1>
  <div class="meta">Domain: ${a.domain} • Created ${new Date(a.created).toLocaleString()} • ${a.sensitive ? 'MARKED SENSITIVE' : ''}</div>

  <div class="section">
    <h2>Situation</h2>
    <div class="card">${escapeHtml(a.scenario)}</div>
  </div>

  <div class="section">
    <h2>Evidence</h2>
    ${evList || '<div class="card"><em>No evidence was recorded.</em></div>'}
  </div>

  <div class="section">
    <h2>Visual Analysis</h2>
    ${visualsSection || '<div class="card"><em>No charts were captured at export time. Re-open the audit in EPISTEME and export again for embedded visuals.</em></div>'}
  </div>

  <div class="section">
    <h2>Sufficiency Rule</h2>
    <div class="card">${escapeHtml(a.sufficiency.textual)}</div>
  </div>

  ${a.decision ? `<div class="section"><h2>Action Taken</h2><div class="card"><strong>${escapeHtml(a.decision.action)}</strong><br><br>${escapeHtml(a.decision.rationale)}</div></div>` : ''}

  <div class="section">
    <h2>Revision Plan</h2>
    <div class="card">
      <strong>Next review:</strong> ${a.revision.nextReview || '—'}<br><br>
      <strong>Falsifiers:</strong><br>${a.revision.falsifiers.map(f => '• ' + escapeHtml(f)).join('<br>') || '<em>none</em>'}<br><br>
      <strong>Monitors:</strong><br>${a.revision.monitors.map(m => '• ' + escapeHtml(m)).join('<br>') || '<em>none</em>'}
    </div>
  </div>

  ${a.outcomes.length > 0 ? `<div class="section"><h2>Outcome History</h2><div class="card">${a.outcomes.map(o => `${new Date(o.loggedAt).toLocaleDateString()}: <strong>${o.result}</strong> — ${escapeHtml(o.outcome)}`).join('<br>')}</div></div>` : ''}

  <details class="section">
    <summary style="cursor:pointer;color:#888">Raw data (JSON) — click to expand</summary>
    <pre>${escapeHtml(json)}</pre>
  </details>

  <footer>
    This document is completely self-contained. All your reasoning lives here.<br>
    Generated by EPISTEME — local-first practical epistemology.
  </footer>
</div>
</body>
</html>`;
}

function download(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function cloneForExport(a: Audit) {
  return JSON.parse(JSON.stringify(a));
}

// Capture current visual state (canvases + trust SVG) for rich exports.
// Only works meaningfully when the workbench is currently rendered.
function captureCurrentVisuals(): { credenceImg: string; monteCarloImg: string; trustSvgDataUrl: string } {
  const credCanvas = document.querySelector('#cred-dist') as HTMLCanvasElement | null;
  const mcCanvas = document.querySelector('#mc-canvas') as HTMLCanvasElement | null;
  const trustContainer = document.querySelector('#trust-graph') as HTMLElement | null;

  let credenceImg = '';
  let monteCarloImg = '';
  let trustSvgDataUrl = '';

  if (credCanvas) {
    try { credenceImg = credCanvas.toDataURL('image/png'); } catch {}
  }
  if (mcCanvas) {
    try { monteCarloImg = mcCanvas.toDataURL('image/png'); } catch {}
  }
  if (trustContainer) {
    const svg = trustContainer.querySelector('svg');
    if (svg) {
      try {
        const serializer = new XMLSerializer();
        const svgStr = serializer.serializeToString(svg);
        const b64 = btoa(unescape(encodeURIComponent(svgStr)));
        trustSvgDataUrl = `data:image/svg+xml;base64,${b64}`;
      } catch {}
    }
  }
  return { credenceImg, monteCarloImg, trustSvgDataUrl };
}

async function showLibrary() {
  hide($('#landing'));
  hide($('#workbench'));
  const lib = $('#library');
  show(lib);

  const list = $('#library-list');
  list.innerHTML = 'Loading…';

  let entries = await listAudits();

  // Simple full-text search (productivity feature)
  const searchInput = document.createElement('input');
  searchInput.placeholder = 'Search claims, scenarios, evidence...';
  searchInput.style.cssText = 'width:100%;margin-bottom:8px;background:#111;border:1px solid #27272a;color:#ddd;padding:6px;border-radius:6px;';
  const container = document.querySelector('#library') as HTMLElement;
  if (container && !container.querySelector('input[placeholder*="Search"]')) {
    container.insertBefore(searchInput, list);
  }
  searchInput.addEventListener('input', () => {
    const q = searchInput.value.toLowerCase();
    const filtered = entries.filter(e => 
      (e.claim + e.scenario).toLowerCase().includes(q)
    );
    renderLibraryList(filtered, list);
  });

  // original list render is now inside renderLibraryList below (with search support)


  if (entries.length === 0) {
    list.innerHTML = `<div class="empty-state">No audits yet on this device. Start one from the landing page.</div>`;
    return;
  }

  for (const e of entries) {
    const card = document.createElement('div');
    card.style.cssText = 'background:#161616;border:1px solid #27272a;border-radius:12px;padding:14px;cursor:pointer;';
    const lockIcon = (e as any).isEncrypted ? ' 🔒' : '';
    card.innerHTML = `
      <div style="display:flex;justify-content:space-between;">
        <div><span style="font-size:11px;opacity:.7;">${e.domain.toUpperCase()}</span> <strong>${escapeHtml(e.claim.slice(0, 92))}${e.claim.length > 92 ? '…' : ''}</strong>${lockIcon}</div>
        <div style="font-size:11px;opacity:.6;white-space:nowrap;">${new Date(e.updated).toLocaleDateString()}</div>
      </div>
      <div style="margin-top:6px;font-size:12px;opacity:.75;">Process: ${e.processScore} • Credence: ${e.currentCredence}% ${e.hasDecision ? '• Committed' : ''}</div>
    `;
    card.addEventListener('click', async () => {
      let full = await loadAudit(e.id);
      if (full && (full as any).isEncrypted) {
        full = await tryLoadEncrypted(e.id);
        if (!full) return; // user cancelled or wrong passphrase
      }
      if (full) {
        currentAudit = full as Audit;
        hide(lib);
        const wb = $('#workbench');
        show(wb);
        wb.classList.add('active');
        renderWorkbench();
      }
    });
    // quick delete
    const del = document.createElement('button');
    del.textContent = 'Delete';
    del.style.cssText = 'float:right;font-size:11px;padding:2px 8px;margin-top:-4px;';
    del.onclick = async (ev) => {
      ev.stopImmediatePropagation();
      if (!confirm('Delete this audit from this device permanently?')) return;
      await deleteAudit(e.id);
      broadcastAuditUpdate(e.id, 'deleted');
      showLibrary(); // refresh
    };
    card.appendChild(del);
    list.appendChild(card);
  }
}

async function showCalibration() {
  // New global calibration dashboard (much more useful than per-audit)
  await showGlobalCalibration();
}

async function showGlobalCalibration() {
  hide($('#landing'));
  hide($('#workbench'));
  const container = $('#library'); // reuse the area for simplicity
  show(container);
  container.innerHTML = `<h2 style="margin-top:0">Your Epistemic Calibration</h2><div id="calib-content">Loading all local audits...</div>`;

  const closeBtn = document.createElement('button');
  closeBtn.textContent = 'Back to home';
  closeBtn.style.marginTop = '20px';
  closeBtn.onclick = () => {
    hide(container);
    show($('#landing'));
    // restore a clean library div for future use
    container.innerHTML = `<h2 style="margin:0 0 12px;">Your audits</h2><div id="library-list" style="display:grid; gap:12px;"></div><div style="margin-top:30px;"><button id="btn-library-close">Back</button></div>`;
    $('#btn-library-close')?.addEventListener('click', () => {
      hide(container);
      show($('#landing'));
    });
  };
  container.appendChild(closeBtn);

  const content = container.querySelector('#calib-content') as HTMLElement;

  try {
    const entries = await listAudits();
    const fullAudits = await Promise.all(entries.map(e => loadAudit(e.id)));

    // Collect all resolved outcomes across everything
    type Resolved = { confidence: number; correct: boolean; date: number; domain: string; claim: string };
    const resolved: Resolved[] = [];

    for (const audit of fullAudits) {
      if (!audit || (audit as any).isEncrypted) continue; // can't read without passphrase for now
      const a = audit as Audit;
      for (const o of a.outcomes) {
        if (o.result === 'pending') continue;
        const correct = o.result === 'confirmed';
        resolved.push({
          confidence: o.updatedCredence ?? a.uncertainty.credence,
          correct,
          date: o.loggedAt,
          domain: a.domain,
          claim: a.claim,
        });
      }
    }

    if (resolved.length === 0) {
      content.innerHTML = `<div class="empty-state">No resolved outcomes logged yet. Go through several audits, log real-world results with "Log outcome / revise", then come back here.</div>`;
      return;
    }

    // Stats + expanded epistemic profile (long-term learning)
    const mod = await import('./epistemic');
    const calib = mod.computeCalibration ? mod.computeCalibration(resolved) : { bins: [], overall: 0 };
    const brier = mod.computeBrierScore ? mod.computeBrierScore(resolved) : 0;
    const reliability = mod.getReliabilityData ? mod.getReliabilityData(resolved) : [];

    // extra profile stats from all audits
    let totalPreMortems = 0, totalBets = 0, avgEvidence = 0;
    for (const audit of fullAudits) {
      if (!audit || (audit as any).isEncrypted) continue;
      const aa = audit as any;
      totalPreMortems += (aa.preMortems || []).length;
      totalBets += (aa.personalBets || []).length;
      avgEvidence += (aa.evidence || []).length;
    }
    avgEvidence = fullAudits.length ? Math.round(avgEvidence / fullAudits.filter(Boolean).length) : 0;

    let html = `<div style="display:flex;gap:24px;flex-wrap:wrap;margin-bottom:16px">
      <div><strong>Resolved predictions:</strong> ${resolved.length}</div>
      <div><strong>Overall accuracy:</strong> ${(calib.overall * 100).toFixed(0)}%</div>
      <div><strong>Brier score</strong> (lower better): ${brier.toFixed(3)}</div>
      <div><strong>Epistemic profile:</strong> ${fullAudits.filter(Boolean).length} audits • avg ${avgEvidence} evidence/audit • ${totalPreMortems} pre-mortems • ${totalBets} personal bets tracked</div>
    </div>`;

    // Reliability diagram canvas
    html += `<div style="margin:12px 0"><strong>Reliability (forecast vs observed)</strong><br><canvas id="reliability-canvas" width="520" height="260" style="background:#111;border:1px solid #27272a;border-radius:6px"></canvas></div>`;

    // Simple history table
    html += `<h3 style="margin-top:20px">Recent logged outcomes</h3><div style="max-height:260px;overflow:auto;font-size:13px">`;
    const sorted = [...resolved].sort((x,y) => y.date - x.date).slice(0, 18);
    html += sorted.map(r => {
      const acc = r.correct ? '✓' : '✗';
      return `<div style="padding:3px 0;border-bottom:1px solid #222">${new Date(r.date).toLocaleDateString()} • ${r.domain} • <span style="color:#aaa">${r.confidence.toFixed(0)}%</span> → ${acc} <span style="opacity:.7">${escapeHtml(r.claim.slice(0,68))}…</span></div>`;
    }).join('');
    html += `</div>`;

    content.innerHTML = html;

    // Draw reliability diagram
    setTimeout(() => {
      const c = document.getElementById('reliability-canvas') as HTMLCanvasElement | null;
      if (!c) return;
      const ctx = c.getContext('2d')!;
      ctx.fillStyle = '#111';
      ctx.fillRect(0, 0, c.width, c.height);

      // Axes
      ctx.strokeStyle = '#333';
      ctx.beginPath();
      ctx.moveTo(40, 20);
      ctx.lineTo(40, 220);
      ctx.lineTo(500, 220);
      ctx.stroke();

      // Perfect calibration line
      ctx.strokeStyle = '#444';
      ctx.beginPath();
      ctx.moveTo(40, 220);
      ctx.lineTo(500, 20);
      ctx.stroke();

      // Our points
      ctx.fillStyle = '#c4b5fd';
      for (const r of reliability) {
        const x = 40 + (r.avgConf / 100) * 440;
        const y = 220 - (r.accuracy * 200);
        ctx.beginPath();
        ctx.arc(x, y, 5, 0, Math.PI * 2);
        ctx.fill();

        // label
        ctx.fillStyle = '#aaa';
        ctx.font = '10px monospace';
        ctx.fillText(`${r.avgConf.toFixed(0)}%`, x - 12, y - 10);
        ctx.fillStyle = '#c4b5fd';
      }

      ctx.fillStyle = '#888';
      ctx.fillText('Perfect calibration', 300, 40);
    }, 40);

  } catch (e) {
    content.innerHTML = `<div class="empty-state">Could not load calibration data. ${String(e)}</div>`;
  }
}

function logOutcomeFlow() {
  if (!currentAudit) return;
  const outcome = prompt('What actually happened? (short description)', '');
  if (!outcome) return;
  const result = (prompt('Result? (confirmed / disconfirmed / mixed / pending)', 'mixed') || 'mixed') as any;
  const lessons = prompt('What did you learn? (optional)', '') || undefined;
  const next = addOutcome(currentAudit, outcome, result, lessons);
  persist(next);
}

// ---------- Encryption / Lock support ----------
async function lockCurrentAuditWithPassphrase() {
  if (!currentAudit) return;
  if (!isCryptoAvailable()) {
    alert('Web Crypto API not available in this browser. Encryption disabled.');
    return;
  }
  const pass1 = prompt('Choose a strong passphrase to encrypt this audit. You will need it every time you open it.\n\nWARNING: If you forget it, the data is permanently unrecoverable.', '');
  if (!pass1) return;
  const pass2 = prompt('Confirm passphrase:', '');
  if (pass1 !== pass2) {
    alert('Passphrases did not match. Nothing was encrypted.');
    return;
  }

  try {
    const payload = await encryptJSON(currentAudit, pass1);
    const header = {
      domain: currentAudit.domain,
      claim: currentAudit.claim,
      updated: currentAudit.updated,
      hasDecision: !!currentAudit.decision,
      currentCredence: currentAudit.uncertainty.credence,
      processScore: computeProcessScore(currentAudit),
      sensitive: currentAudit.sensitive,
    };

    const rec: any = {
      id: currentAudit.id,
      isEncrypted: true,
      header,
      payload,
    };

    await saveEncryptedRecord(rec);
    broadcastAuditUpdate(currentAudit.id);

    alert('Audit encrypted and locked on disk. It will require the passphrase to open from the library.');

    // Clear from current session memory for safety
    currentAudit = null;
    closeWorkbench();
  } catch (e) {
    alert('Encryption failed: ' + e);
  }
}

async function tryLoadEncrypted(id: string): Promise<Audit | null> {
  const stored = await loadAudit(id) as any;
  if (!stored || !stored.isEncrypted) return null;

  const pass = prompt('This audit is encrypted. Enter the passphrase:');
  if (!pass) return null;

  try {
    const plain = await decryptJSON<Audit>(stored.payload, pass);
    // Re-hydrate as current plain audit (user can choose to re-lock later)
    plain.locked = false;
    return plain;
  } catch (e) {
    alert('Wrong passphrase or corrupted data.');
    return null;
  }
}

function openCommandPalette() {
  const cmdk = $('#cmdk');
  const input = $('#cmdk-input') as HTMLInputElement;
  const results = $('#cmdk-results');

  cmdk.classList.add('open');
  input.value = '';
  results.innerHTML = '';

  const commands = [
    { label: 'Add evidence', run: () => { closeCommandPalette(); if (currentAudit) openEvidenceEditor(currentAudit); } },
    { label: 'Add pre-mortem', run: () => { closeCommandPalette(); if (currentAudit) { const t=prompt('Failure scenario?'); if(t) persist(patchAudit(currentAudit, {preMortems: [...(currentAudit.preMortems||[]),t]})); } } },
    { label: 'Take snapshot', run: () => { closeCommandPalette(); takeAndRenderSnapshot(); } },
    { label: 'Export report / bundle', run: () => { closeCommandPalette(); exportCurrentAudit(); } },
    { label: 'Log outcome / revise', run: () => { closeCommandPalette(); logOutcomeFlow(); } },
    { label: 'Show Socratic challenger (?)', run: () => { closeCommandPalette(); showSocraticChallenger(); } },
    { label: 'Open library', run: () => { closeCommandPalette(); showLibrary(); } },
    { label: 'Open calibration dashboard', run: () => { closeCommandPalette(); showGlobalCalibration(); } },
    { label: 'Lock / encrypt current audit', run: () => { closeCommandPalette(); lockCurrentAuditWithPassphrase(); } },
    { label: 'Create personal bet (prediction market lite)', run: () => { closeCommandPalette(); if(currentAudit){ const d=prompt('What are you betting on?'); if(d){ const b={id:crypto.randomUUID(),description:d,credenceAtBet:currentAudit.uncertainty.credence,resolutionDate:currentAudit.revision.nextReview}; persist(patchAudit(currentAudit,{personalBets:[...(currentAudit.personalBets||[]),b]})); } } } },
    { label: 'Close current audit', run: () => { closeCommandPalette(); closeWorkbench(); } },
  ];

  function renderItems(filter = '') {
    results.innerHTML = '';
    commands.filter(c => c.label.toLowerCase().includes(filter.toLowerCase())).forEach((c, i) => {
      const div = document.createElement('div');
      div.className = 'item' + (i === 0 ? ' active' : '');
      div.innerHTML = `<span>${c.label}</span>`;
      div.onclick = () => { closeCommandPalette(); c.run(); };
      results.appendChild(div);
    });
  }

  input.oninput = () => renderItems(input.value);
  input.onkeydown = (e) => {
    if (e.key === 'Escape') closeCommandPalette();
    if (e.key === 'Enter') {
      const first = results.querySelector('.item') as HTMLElement;
      if (first) first.click();
    }
  };

  setTimeout(() => input.focus(), 30);
  renderItems();
}

function closeCommandPalette() {
  $('#cmdk').classList.remove('open');
}

function showSocraticChallenger() {
  const prompts = [
    "What is the single strongest piece of evidence *against* the claim right now? Have you given it equal weight?",
    "If you had to bet your own money at 3:1 odds, which way would you actually bet?",
    "What would the most thoughtful, well-informed person who strongly disagrees with you say is your weakest link?",
    "Which of your assumptions, if false, would flip the entire decision?",
    "Have you searched in the last 48 hours for high-quality disconfirming sources using different keywords?",
    "What is the base rate for situations like this? How does it affect your credence?",
    "If nothing changed in 90 days, would you still be happy with the action you are considering?",
    "What cheap experiment or observation could falsify your current view in the next two weeks?"
  ];
  const p = prompts[Math.floor(Math.random() * prompts.length)];
  const box = document.createElement('div');
  box.style.cssText = 'position:fixed;bottom:20px;right:20px;max-width:380px;background:#161616;border:1px solid #c4b5fd;border-radius:12px;padding:16px;z-index:400;box-shadow:0 10px 30px -10px rgb(0 0 0 / .6)';
  box.innerHTML = `<div style="font-size:13px;font-weight:600;margin-bottom:6px;">Socratic challenger</div><div style="font-size:14px;line-height:1.4;">${p}</div><button style="margin-top:12px;font-size:12px;">Close</button>`;
  box.querySelector('button')!.onclick = () => box.remove();
  document.body.appendChild(box);
}

function closeWorkbench() {
  currentAudit = null;
  const wb = $('#workbench');
  wb.classList.remove('active');
  hide(wb);
  show($('#landing'));
}

// ---------- Boot ----------
init();

// Expose a tiny debug handle (power users / console)
(window as any).EPISTEME = { listAudits, exportAll, importAudits };

// Small escape helper
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}
